import React, { useEffect, useState, useCallback } from 'react'
import { Motion, spring } from 'react-motion'
import cx from 'classnames'
import { bool, object } from 'prop-types'
import Frame from 'react-frame-component'
import networkService from 'src/app/network'
import EventsService from 'src/utils/events-service'
import Storage from 'src/utils/storage'

import { isIE, updateWidgetPopupTimestamp, isWidgetPopupAllowed } from 'src/utils/helpers'
import config from './config'

import Button from '../../build/widget/button'
// TODO: This is why we have that react error (class vs function components)
//  when we add some external component (e.g. Material UI)
//  but importing from source ('src/modules/App') causes other issues
import App from '../../build/widget/app'

import './styles.less'

let windowResizeTimeout = null

const Widget = ({ openOnLoad, colors, sound, css, onIsActiveChange, initialFeature, ...rest }) => {
  const [isActive, setIsActive] = useState(false)
  const [isFullSize, setIsFullSize] = useState(false)
  const [isAnimationActive, setIsAnimationActive] = useState(false)
  // To know when widget popups up for the first time
  const [isFirstActivation, setIsFirstActivation] = useState(true)

  // Check if closed in last 12h
  const isPopupAllowed = isWidgetPopupAllowed()

  // Checks if widget full size is needed based on parent window size
  const updateFullSizeState = (active) => {
    const cls = 'kiosk-widget-mobile-full-size'
    const width = window.innerWidth
    const height = window.innerHeight

    // When widget is open on small screen
    if (active && (width < 450 || (width < 900 && height < 450))) {
      // Add class to main/parent <html> element
      document.documentElement.classList.add(cls)
      setIsFullSize(true)
    } else {
      // Remove class from main/parent <html> element
      document.documentElement.classList.remove(cls)
      setIsFullSize(false)
    }
  }

  // Set animation as active whenever widget is being opened or closed
  const setActiveState = useCallback((active) => {
    if (windowResizeTimeout) {
      clearTimeout(windowResizeTimeout)
    }

    setIsAnimationActive(true)

    setIsActive(active)

    // On open
    if(active === true) {
      if (isFirstActivation === true) {
        // Injects authorization token so that visit event can be correctly registered
        networkService.addAuthorizationHeader()

        EventsService.registerEvent({isWidget: true}, 'access', 'visit')

        setIsFirstActivation(false)

        // Allow for "initial widget feature" to be opened again
        Storage.removeSessionItem('isInitialWidgetFeatureLoaded')
      }
    }

    // On close
    if(active === false) {
      // Reset last closed timestamp
      updateWidgetPopupTimestamp()
    }

    if(onIsActiveChange && typeof onIsActiveChange === 'function') {
      onIsActiveChange(active)
    }
  }, [isFirstActivation, onIsActiveChange])

  useEffect(() => {
    if (isPopupAllowed && openOnLoad) {
      setTimeout(() => {
        if (!isActive) {
          setActiveState(true)
        }
      }, 5000)
    }
  }, [isActive, setActiveState, isPopupAllowed, openOnLoad])

  // Handle window resize
  useEffect(() => {

    // Function to call on window resize
    const windowSizeCalculations = () => {

      if (windowResizeTimeout) {
        clearTimeout(windowResizeTimeout)
      }
      
      // Run with 200ms delay
      windowResizeTimeout = setTimeout(() => {
        updateFullSizeState(isActive)
      }, 200)
    }

    // On mount - or isActive change
    updateFullSizeState(isActive)
    window.addEventListener('resize', windowSizeCalculations)

    // On unmount
    return () => {
      window.removeEventListener('resize', windowSizeCalculations)
    }
  }, [isActive])

  const handleCoreContainerStyles = () => {
    const appContainer = document.querySelector('#kiosk-container')

    //Append core style
    const coreStyleEl = document.createElement('style');
    coreStyleEl.id = "kiosk-core-styles"
    coreStyleEl.appendChild(document.createTextNode(css.coreStyles));
    appContainer.contentDocument.head.appendChild(coreStyleEl);

    //Append branding style
    const brandingStyleEl = document.createElement('style');
    brandingStyleEl.id = "kiosk-branding-color"
    brandingStyleEl.appendChild(document.createTextNode(css.brandingStyles));
    appContainer.contentDocument.head.appendChild(brandingStyleEl);
  }

  const handleButtonContainerStyles = () => {
    const appContainer = document.querySelector('#kiosk-button')

    const buttonStyleEl = document.createElement('style');
    buttonStyleEl.id = "kiosk-button-styles"
    buttonStyleEl.appendChild(document.createTextNode(css.buttonStyles));
    appContainer.contentDocument.head.appendChild(buttonStyleEl);
  }

  const appIframeHolderClassName = cx({
    'kiosk-widget-base-container': true,
    'kiosk-widget-base-container--active--full-size': isActive && isFullSize,
  })

  const appIframeClassName = cx({
    'kiosk-widget-app-iframe': true,
    'kiosk-widget-app-iframe--active--full-size': isActive && isFullSize,
  })

  const buttonClassName = cx({
    'kiosk-widget-button-wrapper': true,
    'kiosk-widget-button-wrapper--active--full-size': isActive && isFullSize,
  })

  const buttonStyle = colors && colors.hasOwnProperty('widgetButtonColor') ? {
    background: colors.widgetButtonColor
  } : {}

  return (
    <React.Fragment>
      {!isIE() && sound && isPopupAllowed && (
        <iframe
          title="notification-sound"
          src={config.notificationSound}
          allow="autoplay"
          id="audio"
          style={{display:"none"}}
        />
      )}
      <Motion
        defaultStyle={{
          y: 40,
          opacity: 0,
        }}
        style={{
          y: spring(isActive ? 0 : 40, config.presets.default),
          opacity: spring(isActive ? 1 : 0, config.presets.default),
        }}
        onRest={() => setIsAnimationActive(false)}
      >
        {interpolatingStyles =>
          <div
            style={{
              display: `${isActive || isAnimationActive ? 'block' : 'none'}`,
            }}
          >
            <div
              className={appIframeHolderClassName}
              style={{
                opacity: interpolatingStyles.opacity,
                transform: `translateY(${interpolatingStyles.y}px)`,
              }}
            >
              <Frame
                className={appIframeClassName}
                id="kiosk-container"
                title="Kiosk app"
                width="100%"
                allowFullScreen
                src="about:blank"
                scrolling="no"
                frameBorder="0"
                contentDidMount={handleCoreContainerStyles}
              >
                <App
                  isWidget={true}
                  isWidgetFullSize={isFullSize}
                  isWidgetActive={isActive}
                  handleCloseWidget={() => setActiveState(false)}
                  initialWidgetFeature={initialFeature}
                  {...rest}
                />
              </Frame>
            </div>
          </div>
        }
      </Motion>
      <Frame
        id="kiosk-button"
        title="kiosk-button"
        className={buttonClassName}
        style={buttonStyle}
        allowFullScreen
        scrolling="no"
        frameBorder="0"
        contentDidMount={handleButtonContainerStyles}
      >
        <Button
          active={isActive}
          count={0}
          onClick={() => setActiveState(!isActive)}
        />
      </Frame>
    </React.Fragment>
  );
}

Widget.propTypes = {
  colors: object.isRequired,
  openOnLoad: bool.isRequired,
  css: object.isRequired,
};

Widget.defaultProps = {
  openOnLoad: false,
  sound: false
}

export default Widget;
